import React, { useCallback, useContext, useState } from 'react';
import getClientMutationId from '../../utils/getClientMutationId';
import {
  Maybe,
  ISalesTransaction,
  ISalesTransactionMutationResult,
  ISalesTransactionMutationVariables,
  TransactionNoteType,
} from '../../graphql/schema/p4types';
import Dropdown from '../../components/connectedForm/Dropdown';
import { BasicHeader } from '../../actions/BasicHeader';
import BlurTextField from '../../components/connectedForm/BlurTextField';
import { BlurTextFieldType } from '../../components/connectedForm/BlurTextField/BlurTextField';
import fieldMetaData from '../../graphql/schema/fieldMetaData';
import getValue from '../../components/connectedForm/utils/getValue';
import { ModalBottomButtonContainer } from '../../components/modals/Styles';
import { Button, SuccessButton } from '../../components/connectedForm/Button';
import * as input from '../../components/connectedForm/inputComponents';
import { default as Form } from '../../components/connectedForm';
import { IFormatterProps } from '../../@types/workspaceTypes';
import { useMutation } from '@apollo/client';
import { useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';
import WorkspaceContext from '../../components/Context/WorkspaceContext';
import SalesTransaction from '../../graphql/entities/salesLedger/SalesTransaction';
import FluentModal from '../../components/modals/FluentModal';

const SalesTransactionNoteModalContent = ({
  rowData,
  onClose,
}: {
  rowData: ISalesTransaction;
  onClose: () => void;
}) => {
  const [note, setNote] = useState<string>(rowData.note);
  const [transactionNoteType, setTransactionNoteType] = useState<Maybe<TransactionNoteType>>(
    rowData.transactionNoteType
  );
  const [excludeFromAutomatedCreditControl, setExcludeFromAutomatedCreditControl] = useState<Maybe<boolean>>(
    rowData?.excludeFromAutomatedCreditControl
  );
  const { reloadData } = useContext(WorkspaceContext);

  const handleQueryError = useComponentQueryErrorHandling({ name: 'Note Update' });
  const [salesTransactionUpdate] = useMutation<ISalesTransactionMutationResult, ISalesTransactionMutationVariables>(
    SalesTransaction.update!,
    {
      onCompleted: () => {
        reloadData?.();
      },
      onError: handleQueryError,
    }
  );

  const isInvoiceOrOpeningBalance = ['SALES_INVOICE', 'SALES_OPENING_BALANCE_INVOICE'].includes(rowData.documentType);

  const onSubmit = useCallback(() => {
    salesTransactionUpdate({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          salesTransaction: {
            id: rowData.id,
            concurrencyVersion: rowData.concurrencyVersion,
            note: note,
            transactionNoteType: transactionNoteType,
            excludeFromAutomatedCreditControl: excludeFromAutomatedCreditControl,
          },
        },
      },
    });
    onClose();
  }, [
    excludeFromAutomatedCreditControl,
    note,
    onClose,
    rowData.concurrencyVersion,
    rowData.id,
    salesTransactionUpdate,
    transactionNoteType,
  ]);

  return (
    <div>
      <BasicHeader>Sales Transaction</BasicHeader>
      <Form.Form>
        <Form.Row>
          <Form.Fieldset cols={1}>
            <Form.Legend label="Notes" />
            <Dropdown
              data-testid={'dropdown-note-type'}
              options={fieldMetaData.TransactionNoteType.enumValues}
              value={transactionNoteType}
              valueOnly={true}
              label={'Reason'}
              onChange={(value) => setTransactionNoteType(getValue(value))}
              valueKey="value"
              labelKey="label"
            />
            <BlurTextField
              data-testid={'textfield-note'}
              value={note}
              label={'Note'}
              onBlur={(value) => setNote(getValue(value))}
              type={BlurTextFieldType.ExpandableTextArea}
              style={{ minHeight: '200px', maxHeight: '200px', overflow: 'auto' }}
            />
          </Form.Fieldset>
          {isInvoiceOrOpeningBalance ? (
            <Form.Fieldset cols={1}>
              <Form.Legend label="Automated Credit Control" />
              <input.Checkbox
                value={!!excludeFromAutomatedCreditControl || transactionNoteType === TransactionNoteType.Disputed}
                label={'Exclude?'}
                onChange={(value) => setExcludeFromAutomatedCreditControl(getValue(value))}
                data-testid={'checkbox-exclude-acc'}
              />
            </Form.Fieldset>
          ) : null}
        </Form.Row>
      </Form.Form>
      <ModalBottomButtonContainer>
        <Button onClick={onClose}>Cancel</Button>
        <SuccessButton onClick={onSubmit}>Update</SuccessButton>
      </ModalBottomButtonContainer>
    </div>
  );
};

const SalesTransactionNoteModalFormatter = (formatterProps: IFormatterProps<ISalesTransaction>) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <div onClick={() => setIsOpen(true)} style={{ width: '100%' }}>
        {formatterProps.value}
        <i className="popoverlink icon">more_vert</i>
      </div>
      <FluentModal isOpen={isOpen}>
        <div style={{ height: '500px', minWidth: '600px' }}>
          {isOpen ? (
            <SalesTransactionNoteModalContent
              rowData={formatterProps.dependentValues.rowData}
              onClose={() => setIsOpen(false)}
            />
          ) : null}
        </div>
      </FluentModal>
    </>
  );
};

export default SalesTransactionNoteModalFormatter;
